import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, Chip, IconButton, LinearProgress } from '@mui/material'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import Breadcrumb from '../../../components/Breadcrumb'
import { VIDEO, HOMEWORK, VIDEONOTES } from '../../../utils/constants'
import VideoTab from './videoTab/VideoTab'
import HomeworkTab from './homeworkTab/HomeworkTab'
import VideonotesTab from './videonotesTab/VideonotesTab'
import SingleSelect from '../../../components/muiComponents/singleSelect/SingleSelect'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import { ui } from '../../../utils/uiTokens'
import userIcon from '../../../assets/img/avatar.jpg'
import { H3, Body1 } from '../../../layouts/TextStyles'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CuratorStudentState } from '../../../features/types'
import { capitalize } from '../../../utils/functions'
import HistoryIcon from '@mui/icons-material/History';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { toast } from 'react-toastify'

const StudentProgressPage = () => {
    const { formatMessage, locale } = useIntl()
    const { state } = useLocation()
    const { userCourseEnrollId } = useParams()
    const [subjectId, setSubjectId] = useState<number | null>(null)
    const [monthNumber, setMonthNumber] = useState<number | undefined>()
    const [weekNumber, setWeekNumber] = useState<number | undefined>()
    const [searchParams, setSearchParams] = useSearchParams(new URLSearchParams('type=VIDEO'))
    const { data: studentProgress = {} as CuratorStudentState, isFetching: loadingStudentProgress, refetch: refetchStudentProgress } =
        curatorStudentsApiSlice.useFetchStudentProgressQuery({ userCourseEnrollId: +userCourseEnrollId })
    const [updateStudentPerformance, { isLoading: loadingUpdateStudentPerformance }] = curatorStudentsApiSlice.useUpdateStudentPerformanceMutation()
    const urlFullName = searchParams.get('fullName')
    const fullName = state?.student
        ? state.student.firstName + ' ' + state.student.lastName
        : urlFullName
            ? decodeURIComponent(urlFullName).replace(/-/g, ' ')
            : ''

    const handlePerformanceLevelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newPerformanceLevel = e.target.value
        try {
            await updateStudentPerformance({ userCourseEnrollId: +userCourseEnrollId, performanceLevel: newPerformanceLevel }).unwrap()
            await refetchStudentProgress()
            toast.success(formatMessage({ id: 'performanceLevelUpdated' }))
        } catch (error) {
            console.error(error)
            toast.error(formatMessage({ id: 'error' }))
        }
    }

    const handleTabChange = (type: string) => {
        const p = new URLSearchParams(searchParams);
        p.set('type', type);
        setSearchParams(p, { replace: true });
    };

    const handleSubjectClick = (subjectId: number) => {
        setSubjectId(subjectId)
    }

    const getDuration = (daysLeft: number) => {
        if (daysLeft > 30) {
            return (
                <CourseInfoSpan color={ui.pink} backgroundColor={ui.pinkSoft}>
                    <Medium color={ui.pink}>
                        {Math.floor(daysLeft / 30) + ' ' + formatMessage({ id: 'month' })}
                    </Medium>
                    <DayLabel>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        {daysLeft % 30 + ' ' + formatMessage({ id: 'day' })}
                    </DayLabel>
                </CourseInfoSpan>)
        } else if (daysLeft === 0) {
            return <FinishedLabel>✓ {formatMessage({ id: 'courseCompleted' })}</FinishedLabel>
        } else {
            return (
                <CourseInfoSpan color={ui.pink} backgroundColor={ui.pinkSoft}>
                    <DayLabel>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        {daysLeft + ' ' + formatMessage({ id: 'day' })}
                    </DayLabel>
                </CourseInfoSpan>
            )
        }
    }

    // Куратор ведёт не все предметы студента: показываем только свои.
    // Если бэкенд не отметил ни одного (нет привязки), падаем обратно на все предметы.
    const subjects = useMemo(() => {
        const allSubjects = studentProgress?.subjects || []
        const curatorSubjects = allSubjects.filter((subject) => subject.curatorSubject)

        return curatorSubjects.length ? curatorSubjects : allSubjects
    }, [studentProgress?.subjects])

    useEffect(() => {
        if (subjects.length && !subjectId) {
            const urlSubjectId = Number(searchParams.get('courseSubjectId'))
            const urlSubject = subjects.find(
                (subject) => subject.id === urlSubjectId,
            )

            setSubjectId(urlSubject ? urlSubject.id : subjects[0].id)
        }
    }, [subjects, subjectId, searchParams])


    return (
        <Wrapper>
            <Box sx={styles.boxContainer}>
                <Breadcrumb
                    links={[
                        formatMessage({ id: 'groups' }),
                        formatMessage({ id: 'student' }),
                        fullName
                    ]}
                />
            </Box>

            {loadingStudentProgress ? (
                <LinearProgress />
            ) : (
                <Content>
                    <StudentCard>
                        <Box>
                            <img src={userIcon} alt="user" width={140} height={132} style={{ borderRadius: '24px' }} />
                        </Box>
                        <Box sx={{ ...styles.studentInfo, marginLeft: '20px', marginRight: 'auto' }}>
                            <H3>{studentProgress?.firstName} {studentProgress?.lastName}</H3>
                            <Body1>{formatMessage({ id: 'birthDate' })}{' : '}<span style={{ fontWeight: 600, color: '#0D894F' }}>{studentProgress?.birthDate}</span></Body1>
                            <Body1>{formatMessage({ id: 'mobileNumber' })}{' : '}<span style={{ fontWeight: 600, color: '#0D894F' }}>{studentProgress?.mobile}</span></Body1>
                            <Body1>{formatMessage({ id: 'parentMobileNumber' })}{' : '}<span style={{ fontWeight: 600, color: '#0D894F' }}>{studentProgress?.parentMobile}</span></Body1>
                        </Box>
                        <Box sx={{ ...styles.studentInfo, alignItems: 'flex-end' }}>
                            <Button variant="contained" color="primary" startIcon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />} disabled>
                                {formatMessage({ id: 'note' })}
                            </Button>
                            <StatusSelect
                                value={studentProgress?.performanceLevel || formatMessage({ id: 'performance' })}
                                performanceLevel={studentProgress?.performanceLevel || ''}
                                onChange={handlePerformanceLevelChange}
                                disabled={loadingUpdateStudentPerformance}>
                                <option disabled selected value={formatMessage({ id: 'performance' })}>{formatMessage({ id: 'performance' })}</option>
                                {studentProgress?.performances?.map((performance) => (
                                    <option key={performance} value={performance}>{formatMessage({ id: `${performance}` })}</option>
                                ))}
                            </StatusSelect>
                            <IconButton sx={styles.editIcon} disabled><EditOutlinedIcon /></IconButton>
                        </Box>
                    </StudentCard>
                    <CourseInfoCard>
                        <Box>
                            <Medium color='#333843'>{formatMessage({ id: 'courseInfo' })}</Medium>
                            <Box sx={styles.courseInfoBox}>
                                <CourseInfoSpan color="#0D894F" backgroundColor="#E7F4EE">
                                    <VerifiedOutlinedIcon sx={{ fontSize: 18 }} />
                                    {studentProgress?.[`coursesTitle${capitalize(locale)}`]}
                                </CourseInfoSpan>
                                {studentProgress?.daysLeft !== undefined && (
                                    getDuration(studentProgress?.daysLeft)
                                )}

                            </Box>
                        </Box>
                        <Box>
                            <Medium color='#333843'>{formatMessage({ id: 'subjects' })}</Medium>
                            <Box sx={styles.courseInfoBox}>
                                {subjects.map((subject) => (
                                    <Chip key={subject.id}
                                        label={subject?.[`title${capitalize(locale)}`]}
                                        sx={{
                                            cursor: 'pointer',
                                            fontWeight: subjectId === subject.id ? 600 : 400,
                                            backgroundColor: subjectId === subject.id ? ui.pinkSoft : 'transparent',
                                            border: `1px solid ${subjectId === subject.id ? 'transparent' : ui.border}`,
                                            color: subjectId === subject.id ? ui.pink : ui.sub,
                                            '&:hover': { backgroundColor: ui.pinkSoft, color: ui.pink },
                                        }}
                                        onClick={() => handleSubjectClick(subject.id)}
                                    />
                                ))}
                            </Box>
                        </Box>

                    </CourseInfoCard>

                    <Box sx={{ flexDirection: 'column', display: 'flex', gridColumn: '1 / 3' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            {/* Фильтр только у вкладок с процентами: домашка и видеозаметки не по темам */}
                            {(searchParams.get('type') || VIDEO) === VIDEO ? (
                                <MonthWeekFilter
                                    monthNumber={monthNumber}
                                    weekNumber={weekNumber}
                                    onChange={(month, week) => {
                                        setMonthNumber(month)
                                        setWeekNumber(week)
                                    }}
                                />
                            ) : (
                                <Box />
                            )}

                        <Tabs>
                            <Tab
                                searchParams={searchParams.get('type')}
                                type={VIDEO}
                                onClick={() => handleTabChange(VIDEO)}
                            >
                                {formatMessage({ id: 'video' })}
                            </Tab>

                            <Tab
                                searchParams={searchParams.get('type')}
                                type={HOMEWORK}
                                onClick={() => handleTabChange(HOMEWORK)}
                            >
                                {formatMessage({ id: 'homework' })}
                            </Tab>

                            <Tab
                                searchParams={searchParams.get('type')}
                                type={VIDEONOTES}
                                onClick={() => handleTabChange(VIDEONOTES)}
                            >
                                {formatMessage({ id: 'videonotes' })}
                            </Tab>
                        </Tabs>
                        </Box>
                        {searchParams.get('type') === VIDEO && studentProgress?.userId && (
                            <VideoTab
                                studentId={+studentProgress?.userId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                                monthNumber={monthNumber}
                                weekNumber={weekNumber}
                            />
                        )}
                        {searchParams.get('type') === HOMEWORK && (
                            <HomeworkTab
                                userCourseEnrollId={+userCourseEnrollId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                            />
                        )}
                        {searchParams.get('type') === VIDEONOTES && (
                            <VideonotesTab
                                userCourseEnrollId={+userCourseEnrollId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                            />
                        )}
                    </Box>
                </Content>
            )}
        </Wrapper>
    )
}

export default StudentProgressPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Content = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: max-content 1fr;
    gap: 16px;
    background-color: #fff;
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;

    @media screen and (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`

// Сегментированный переключатель, как пилюли недель в фильтре
const Tabs = styled.div`
    margin-left: auto;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
    cursor: pointer;
`

const Tab = styled.div<{ searchParams: string; type: string }>`
    ${(props) => {
        const active =
            props.searchParams == props.type ||
            (props.type == VIDEO && props.searchParams == null)

        return `
            background: ${active ? ui.pinkSoft : 'transparent'};
            border: 1px solid ${active ? 'transparent' : ui.border};
            color: ${active ? ui.pink : ui.sub};
            font-weight: ${active ? 600 : 400};
        `
    }}
    border-radius: 12px;
    padding: 8px 16px;
    font-size: 13.5px;
    line-height: 1.2;
    white-space: nowrap;

    &:hover {
        border-color: ${ui.pink};
        color: ${ui.pink};
    }
`

const StudentCard = styled.div`
    display: flex;
    align-items: center;
    padding: 20px;
    border-radius: 16px;
    border: 1px solid ${ui.border};
    background-color: ${ui.card};
`

const CourseInfoCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    border-radius: 16px;
    border: 1px solid ${ui.border};
    background-color: ${ui.card};
`

const CourseInfoSpan = styled.span<{ color: string, backgroundColor: string }>`
    color: ${({ color }) => color};
    background-color: ${({ backgroundColor }) => backgroundColor};
    padding: 6px 12px;
    border-radius: 24px;
    font-weight: 600;
    font-size: 16px;
    display: flex;
    text-align: center;
    gap: 8px;
`

const StatusSelect = styled.select<{ performanceLevel: string }>`
    background-color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return '#E7F4EE';
            case 'MEDIUM':
                return '#E8F8FD';
            case 'LOW':
                return '#FDF1E8';
            case 'NEEDS_HELP':
                return '#FEEDEC';
            default:
                return '#E0E2E7';
        }
    }};
    color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return '#1B998B';
            case 'MEDIUM':
                return '#13B2E4';
            case 'LOW':
                return '#E46A11';
            case 'NEEDS_HELP':
                return '#F04438';
            default:
                return '#434343';
        }
    }};
    border-radius: 100px;
    padding: 6px 0;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
    border: none;
    outline: none;
    cursor: pointer;
`

const Medium = styled.span<{ color: string }>`
    font-size: 16px;
    font-weight: 600;
    color: ${({ color }) => color};
`

const DayLabel = styled.span`
    font-size: 14px;
    color: #667085;
    display: flex;
    align-items: center;
    gap: 2px;
`

const FinishedLabel = styled.span`
    color: #0D894F;
    font-weight: 500;
    font-size: 16px;
    background-color: #CFE7DC;
    border: 4px solid #E7F4EE;
    border-radius: 100px;
    padding: 2px 12px;
`

const styles = {
    boxContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        mb: 1,
    },
    studentInfo: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
    },
    courseInfoBox: {
        marginTop: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    editIcon: {
        background: ui.pinkSoft,
        borderRadius: '10px',
        color: ui.pink,
        width: '42px',
        height: '42px',
    },
}